import { computed, reactive, Ref, ref } from '@vue/composition-api';
import { i18n_t } from '@/locales/i18n';
import {
  ConflictStrategy,
  CoSyncInfo,
  CoSyncSetting,
  CoSyncStrategy,
  CoSyncTimingType,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';

export type ShowSettingSource =
  | { source: 'create' }
  | { source: 'setting'; coSyncInfo: CoSyncInfo }
  | { source: 'none' };

export function useCoSyncSettingDrawer() {
  const showSettingSource: Ref<ShowSettingSource> = ref<ShowSettingSource>({ source: 'none' });

  const buildDefaultSettings = () => ({
    bidirection: {
      strategy: CoSyncStrategy.BOTH,
      timingType: CoSyncTimingType.AUTO,
      conflictStrategy: ConflictStrategy.ASK,
      keepWhichLastStatus: 'started',
    } as CoSyncSetting,
    uniUpload: {
      strategy: CoSyncStrategy.LOCAL_TO_CLOUD,
      timingType: CoSyncTimingType.AUTO,
      conflictStrategy: ConflictStrategy.ASK,
      keepWhichLastStatus: 'started',
    } as CoSyncSetting,
    uniDownload: {
      strategy: CoSyncStrategy.CLOUD_TO_LOCAL,
      timingType: CoSyncTimingType.AUTO,
      conflictStrategy: ConflictStrategy.ASK,
      keepWhichLastStatus: 'started',
    } as CoSyncSetting,
  });

  const settings = reactive(buildDefaultSettings());

  const defaultSyncType = CoSyncStrategy.BOTH;
  const syncType = ref(defaultSyncType);

  const selectedCoSyncSetting = computed<CoSyncSetting>((): CoSyncSetting => {
    if (syncType.value === CoSyncStrategy.BOTH) return settings.bidirection;
    if (syncType.value === CoSyncStrategy.CLOUD_TO_LOCAL) return settings.uniDownload;
    return settings.uniUpload;
  });

  const conflictStrategyWording = computed(() => {
    if (selectedCoSyncSetting.value.conflictStrategy === 'ask') return i18n_t('同步冲突：总是询问我');
    if (selectedCoSyncSetting.value.conflictStrategy === 'use_local') return i18n_t('同步冲突：使用本地版本');
    if (selectedCoSyncSetting.value.conflictStrategy === 'use_remote') return i18n_t('同步冲突：使用云端版本');
    return i18n_t('同步冲突：保留全部文件');
  });

  const timingTypeWording = computed(() => {
    if (selectedCoSyncSetting.value.timingType === CoSyncTimingType.AUTO) return i18n_t('自动同步');
    if (selectedCoSyncSetting.value.timingType === CoSyncTimingType.MANUL) return i18n_t('手动同步');
    return i18n_t('定时同步');
  });

  const methods = {
    setup(setting: CoSyncSetting) {
      if (setting.strategy === CoSyncStrategy.BOTH) settings.bidirection = setting;
      else if (setting.strategy === CoSyncStrategy.CLOUD_TO_LOCAL) settings.uniDownload = setting;
      else settings.uniUpload = setting;

      syncType.value = setting.strategy;
    },

    reset() {
      showSettingSource.value = { source: 'none' };
      Object.assign(settings, buildDefaultSettings());
      syncType.value = defaultSyncType;
    },

    onSettingUpdate(setting: CoSyncSetting) {
      methods.setup(setting);
    },

    updateSyncType(aSyncType: CoSyncStrategy) {
      syncType.value = aSyncType;
    },
  };

  return {
    showSettingSource,
    selectedCoSyncSetting,

    conflictStrategyWording,
    timingTypeWording,

    methods,
  };
}
